<template>
  <div class="theme-decorator-container" :class="[skinId || 'skin-default', typeof size === 'string' && isNaN(size) ? size : '', { 'has-canvas': showCanvas }]" :style="decoratorStyles">
    <!-- Canvas background layer -->
    <canvas
      v-if="showCanvas"
      ref="canvasRef"
      class="decorator-canvas"
    ></canvas>

    <!-- Avatar frame wrapper -->
    <div v-if="$slots.avatar" class="avatar-frame-wrapper" :class="[skinId || 'skin-default']">
      <slot name="avatar"></slot>
    </div>

    <!-- Nickname wrapper -->
    <div v-if="$slots.nickname" class="nickname-wrapper">
      <span class="nickname-display" :class="skinId || 'skin-default'">
        <slot name="nickname"></slot>
      </span>
      <span v-if="badgeText" class="user-badge-tag" :class="skinId">
        {{ badgeText }}
      </span>
    </div>

    <!-- Default wrapper for bubbles or custom structures -->
    <div v-if="$slots.default" class="decorator-content-wrapper">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import skinVisualConfigCache from '@/utils/skinVisualConfigCache';

// ============================================================
// 皮肤视觉渲染器（配置驱动）
// 原 12 款硬编码 case 提炼为 preset 渲染表；运行时按 visualConfig.preset 分发。
// 每款 visualConfig 来自 skinVisualConfigCache（内置兜底 + 管理端覆盖）。
// ============================================================

// 已注入的动态 CSS skinId 集合（页面级共享，避免重复注入）
const _injectedSkinStyles = new Set();

// 粒子速度全局缩放因子（校准至与旧版硬编码一致的视觉速度）
const PARTICLE_SPEED_SCALE = 0.5;

// 头像框动画 keyframes 定义表（与 visualConfig.avatarFrame.animation 对应）
const AVATAR_FRAME_KEYFRAMES = {
  'spin': '@keyframes td-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }',
  'pulse-ring': '@keyframes td-pulse-ring { 0% { transform: scale(0.98); box-shadow: 0 0 0 0 rgba(255, 0, 127, 0.4); } 50% { transform: scale(1.02); box-shadow: 0 0 10px 2px rgba(127, 0, 255, 0.6); } 100% { transform: scale(0.98); box-shadow: 0 0 0 0 rgba(255, 0, 127, 0.4); } }',
  'star-glow': '@keyframes td-star-glow { 0%, 100% { box-shadow: 0 0 5px rgba(0, 198, 255, 0.4); } 50% { box-shadow: 0 0 12px rgba(253, 236, 110, 0.8); } }',
  'pulse-fire': '@keyframes td-pulse-fire { 0%, 100% { box-shadow: 0 0 5px rgba(255, 69, 0, 0.4); } 50% { box-shadow: 0 0 14px rgba(255, 215, 0, 0.8); } }',
  'thunder-flash': '@keyframes td-thunder-flash { 0%, 100% { box-shadow: 0 0 4px rgba(108, 180, 255, 0.4); } 45%, 55% { box-shadow: 0 0 16px rgba(190, 225, 255, 0.95); } }',
  'ink-breathe': '@keyframes td-ink-breathe { 0%, 100% { box-shadow: 0 0 3px rgba(30, 30, 30, 0.5); } 50% { box-shadow: 0 0 10px rgba(80, 80, 80, 0.8); } }',
  'firework-glow': '@keyframes td-firework-glow { 0% { box-shadow: 0 0 5px rgba(255, 20, 147, 0.5); } 33% { box-shadow: 0 0 14px rgba(255, 215, 0, 0.85); } 66% { box-shadow: 0 0 12px rgba(0, 255, 127, 0.7); } 100% { box-shadow: 0 0 5px rgba(255, 20, 147, 0.5); } }'
};

// 头像框 animation 名称映射（visualConfig.animation → CSS animation-name）
const ANIMATION_NAME_MAP = {
  'spin': 'td-spin',
  'pulse-ring': 'td-pulse-ring',
  'star-glow': 'td-star-glow',
  'pulse-fire': 'td-pulse-fire',
  'thunder-flash': 'td-thunder-flash',
  'ink-breathe': 'td-ink-breathe',
  'firework-glow': 'td-firework-glow'
};

/**
 * 构造指定皮肤的动态 CSS 文本（头像框 + 气泡 + 必要 keyframes）。
 * 全局选择器（无 scoped），特异性 (0,3,1) 与原硬编码 scoped 选择器等价；
 * 因我们同时移除了 scoped 中 12 款硬编码块，不会冲突。
 */
function buildSkinCssText(skinId, vc) {
  const parts = [];
  const injectedKeyframes = new Set();

  // 1. 头像框 ::after
  if (vc.avatarFrame) {
    const af = vc.avatarFrame;
    const grad = (af.gradient && af.gradient.length > 0) ? af.gradient.join(', ') : 'transparent';
    const pad = (af.padding !== undefined && af.padding !== null) ? af.padding : 3;
    const animName = ANIMATION_NAME_MAP[af.animation];
    const animDecl = (animName && af.animationDuration > 0)
      ? `animation: ${animName} ${Number(af.animationDuration)}s linear infinite;`
      : '';
    if (animName && !injectedKeyframes.has(animName)) {
      parts.push(AVATAR_FRAME_KEYFRAMES[af.animation]);
      injectedKeyframes.add(animName);
    }
    parts.push(`
.theme-decorator-container .avatar-frame-wrapper.${skinId}::after {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  border-radius: 50%;
  padding: ${pad}px;
  background: linear-gradient(135deg, ${grad});
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  ${animDecl}
}`);
  }

  // 2. 聊天气泡
  if (vc.bubble) {
    const b = vc.bubble;
    const grad = (b.bgGradient && b.bgGradient.length > 0) ? b.bgGradient.join(', ') : 'transparent';
    parts.push(`
.theme-decorator-container .chat-bubble-custom.${skinId} {
  background: linear-gradient(135deg, ${grad}) !important;
  border: 1px solid ${b.borderColor || 'transparent'} !important;
  box-shadow: 0 0 10px ${b.shadowColor || 'transparent'} !important;
  color: ${b.textColor || '#000'} !important;
}`);
    if (b.darkTextColor) {
      parts.push(`
body.dark-theme .theme-decorator-container .chat-bubble-custom.${skinId} {
  color: ${b.darkTextColor} !important;
}`);
    }
  }

  return parts.join('\n');
}

// ============================================================
// 粒子 preset 渲染器表
// 每个 preset 提供两个函数：create(randomY, ctx, cfg, w, h) 与 update(p, ctx, cfg, w, h, recreate)
// create 返回新粒子对象；update 返回 undefined 表示当前粒子继续，返回 object 表示替换为新粒子。
// ============================================================
const PRESET_RENDERERS = {
  // glow: 大型径向光晕，缓慢飘移，边界反弹（aurora）
  glow: {
    create: (randomY, _ctx, cfg, w, h) => {
      const colors = cfg.colors || ['0, 243, 255'];
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4 * (cfg.speed || 1),
        vy: (Math.random() - 0.5) * 0.4 * (cfg.speed || 1),
        radius: Math.random() * (cfg.size || 25) + (cfg.size || 25) * 0.8,
        opacity: Math.random() * (cfg.opacity || 0.15) + 0.05,
        color: colors[Math.floor(Math.random() * colors.length)],
        angle: Math.random() * Math.PI * 2
      };
    },
    update: (p, ctx, _cfg, w, h) => {
      p.x += p.vx;
      p.y += p.vy;
      p.angle += 0.005;
      if (p.x < -p.radius) p.x = w + p.radius;
      if (p.x > w + p.radius) p.x = -p.radius;
      if (p.y < -p.radius) p.y = h + p.radius;
      if (p.y > h + p.radius) p.y = -p.radius;
      const radGrad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius);
      radGrad.addColorStop(0, `rgba(${p.color}, ${p.opacity})`);
      radGrad.addColorStop(0.5, `rgba(${p.color}, ${p.opacity * 0.4})`);
      radGrad.addColorStop(1, `rgba(${p.color}, 0)`);
      ctx.fillStyle = radGrad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
    }
  },

  // stream: 数字字符流下落（cyber）
  stream: {
    create: (randomY, _ctx, cfg, w, h) => {
      const colors = cfg.colors || ['#ff007f'];
      return {
        x: Math.random() * w,
        y: randomY ? Math.random() * h : -10,
        vy: (Math.random() * 1.2 + 0.8) * (cfg.speed || 1),
        length: Math.random() * (cfg.size || 15) + (cfg.size || 15) * 0.6,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * (cfg.opacity || 0.4) + 0.2,
        char: String.fromCharCode(33 + Math.floor(Math.random() * 90))
      };
    },
    update: (p, ctx, _cfg, _w, h, recreate) => {
      p.y += p.vy;
      if (p.y > h + 15) return recreate();
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = p.opacity;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - p.length);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.font = '7px Courier New';
      ctx.fillText(p.char, p.x - 2, p.y + 4);
      ctx.globalAlpha = 1.0;
    }
  },

  // petal: 椭圆花瓣下落+旋转摇摆（sakura）
  petal: {
    create: (randomY, _ctx, cfg, w, h) => {
      const colors = cfg.colors || ['255, 183, 197'];
      return {
        x: Math.random() * w,
        y: randomY ? Math.random() * h : -10,
        vx: (Math.random() * 0.5 + 0.2) * (cfg.speed || 1),
        vy: (Math.random() * 0.6 + 0.4) * (cfg.speed || 1),
        radius: Math.random() * (cfg.size || 4) + (cfg.size || 4) * 0.75,
        color: colors[Math.floor(Math.random() * colors.length)],
        angle: Math.random() * Math.PI * 2,
        spinSpeed: (Math.random() - 0.5) * 0.05,
        opacity: cfg.opacity || 0.75
      };
    },
    update: (p, ctx, _cfg, w, h, recreate) => {
      p.y += p.vy;
      p.x += p.vx + Math.sin(p.angle) * 0.3;
      p.angle += p.spinSpeed;
      if (p.y > h + 10 || p.x > w + 10) return recreate();
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.fillStyle = `rgba(${p.color}, ${p.opacity})`;
      ctx.beginPath();
      ctx.ellipse(0, 0, p.radius * 1.5, p.radius * 0.8, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  },

  // spark: 上升小亮点+闪烁（starry）
  spark: {
    create: (randomY, _ctx, cfg, w, h) => {
      const colors = cfg.colors || ['255, 215, 0'];
      return {
        x: Math.random() * w,
        y: randomY ? Math.random() * h : h + 10,
        vx: (Math.random() - 0.5) * 0.3 * (cfg.speed || 1),
        vy: -(Math.random() * 0.5 + 0.3) * (cfg.speed || 1),
        radius: Math.random() * (cfg.size || 1.5) + (cfg.size || 1.5) * 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * (cfg.opacity || 0.6) + 0.2,
        blinkSpeed: Math.random() * 0.03 + 0.01,
        blinkDir: Math.random() > 0.5 ? 1 : -1
      };
    },
    update: (p, ctx, _cfg, _w, h, recreate) => {
      p.y += p.vy;
      p.x += p.vx;
      p.opacity += p.blinkSpeed * p.blinkDir;
      if (p.opacity >= 0.8) { p.opacity = 0.8; p.blinkDir = -1; }
      else if (p.opacity <= 0.1) { p.opacity = 0.1; p.blinkDir = 1; }
      if (p.y < -10 || p.x < -10 || p.x > h + 10) return recreate();
      ctx.save();
      ctx.fillStyle = `rgba(${p.color}, ${p.opacity})`;
      ctx.shadowColor = `rgba(${p.color}, 0.8)`;
      ctx.shadowBlur = 4;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  },

  // smoke: 烟雾上升+扩散渐隐（abyss）
  smoke: {
    create: (randomY, _ctx, cfg, w, h) => {
      const colors = cfg.colors || ['138, 43, 226'];
      return {
        x: Math.random() * w,
        y: randomY ? Math.random() * h : h + 10,
        vx: (Math.random() - 0.5) * 0.2 * (cfg.speed || 1),
        vy: -(Math.random() * 0.4 + 0.2) * (cfg.speed || 1),
        radius: Math.random() * (cfg.size || 12) + (cfg.size || 12) * 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * (cfg.opacity || 0.2) + 0.05,
        expandSpeed: Math.random() * 0.04 + 0.02
      };
    },
    update: (p, ctx, _cfg, _w, _h, recreate) => {
      p.y += p.vy;
      p.x += p.vx;
      p.radius += p.expandSpeed;
      p.opacity -= 0.0015;
      if (p.y < -p.radius || p.opacity <= 0) return recreate();
      const smokeGrad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius);
      smokeGrad.addColorStop(0, `rgba(${p.color}, ${p.opacity})`);
      smokeGrad.addColorStop(0.6, `rgba(${p.color}, ${p.opacity * 0.3})`);
      smokeGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = smokeGrad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
    }
  },

  // sparkle: 下落亮点+闪烁（gold）
  sparkle: {
    create: (randomY, _ctx, cfg, w, h) => {
      const colors = cfg.colors || ['255, 215, 0'];
      return {
        x: Math.random() * w,
        y: randomY ? Math.random() * h : -10,
        vx: (Math.random() - 0.5) * 0.2 * (cfg.speed || 1),
        vy: (Math.random() * 0.8 + 0.5) * (cfg.speed || 1),
        radius: Math.random() * (cfg.size || 1.5) + (cfg.size || 1.5) * 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * (cfg.opacity || 0.4) + 0.4,
        blinkSpeed: Math.random() * 0.04 + 0.02,
        blinkDir: Math.random() > 0.5 ? 1 : -1
      };
    },
    update: (p, ctx, _cfg, _w, h, recreate) => {
      p.x += p.vx;
      p.y += p.vy;
      p.opacity += p.blinkSpeed * p.blinkDir;
      if (p.opacity >= 0.9) { p.opacity = 0.9; p.blinkDir = -1; }
      else if (p.opacity <= 0.2) { p.opacity = 0.2; p.blinkDir = 1; }
      if (p.y > h + 10) return recreate();
      ctx.save();
      ctx.fillStyle = `rgba(${p.color}, ${p.opacity})`;
      ctx.shadowColor = `rgba(${p.color}, 0.9)`;
      ctx.shadowBlur = 5;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  },

  // ember: 上升火苗+颜色生命周期渐变（phoenix）
  ember: {
    create: (randomY, _ctx, cfg, w, h) => {
      const colors = cfg.colors || ['255, 215, 0', '60, 80, 80'];
      return {
        x: Math.random() * w,
        y: randomY ? Math.random() * h : h + 10,
        vx: (Math.random() - 0.5) * 0.4 * (cfg.speed || 1),
        vy: -(Math.random() * 1.2 + 0.8) * (cfg.speed || 1),
        radius: Math.random() * (cfg.size || 2) + (cfg.size || 2) * 0.75,
        life: 0,
        maxLife: Math.random() * 0.5 + 0.5,
        colorStart: colors[0] || '255, 215, 0',
        colorEnd: colors[1] || '60, 80, 80',
        baseOpacity: cfg.opacity || 1
      };
    },
    update: (p, ctx, _cfg, _w, _h, recreate) => {
      p.x += p.vx;
      p.y += p.vy;
      p.life += 0.025;
      if (p.y < -10 || p.life >= p.maxLife) return recreate();
      const t = p.life / p.maxLife;
      const parseRGB = (s) => s.split(',').map(n => parseInt(n.trim(), 10));
      const [r1, g1, b1] = parseRGB(p.colorStart);
      const [r2, g2, b2] = parseRGB(p.colorEnd);
      const r = Math.round(r1 + (r2 - r1) * t);
      const g = Math.round(g1 + (g2 - g1) * t);
      const b = Math.round(b1 + (b2 - b1) * t);
      const alpha = (1 - t * 0.6) * p.baseOpacity;
      ctx.save();
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
      ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.9)`;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  },

  // bubble: 气泡上升+摇摆+高光（ocean）
  bubble: {
    create: (randomY, _ctx, cfg, w, h) => ({
      x: Math.random() * w,
      y: randomY ? Math.random() * h : h + 10,
      vx: 0,
      vy: -(Math.random() * 0.6 + 0.4) * (cfg.speed || 1),
      radius: Math.random() * (cfg.size || 3) + (cfg.size || 3) * 0.7,
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: Math.random() * 0.05 + 0.03,
      wobbleAmp: Math.random() * 0.6 + 0.3,
      opacity: cfg.opacity || 0.6
    }),
    update: (p, ctx, _cfg, _w, _h, recreate) => {
      p.wobble += p.wobbleSpeed;
      p.x += Math.sin(p.wobble) * p.wobbleAmp;
      p.y += p.vy;
      if (p.y < -p.radius - 5) return recreate();
      ctx.save();
      ctx.globalAlpha = p.opacity;
      const bubbleGrad = ctx.createRadialGradient(
        p.x - p.radius * 0.3, p.y - p.radius * 0.3, 0,
        p.x, p.y, p.radius
      );
      bubbleGrad.addColorStop(0, 'rgba(180, 230, 255, 0.6)');
      bubbleGrad.addColorStop(0.7, 'rgba(0, 206, 209, 0.3)');
      bubbleGrad.addColorStop(1, 'rgba(0, 119, 190, 0.15)');
      ctx.fillStyle = bubbleGrad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(120, 200, 255, 0.7)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.beginPath();
      ctx.arc(p.x - p.radius * 0.35, p.y - p.radius * 0.35, p.radius * 0.25, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  },

  // lightning: 折线闪电（thunder）
  lightning: {
    create: (randomY, _ctx, cfg, w, h) => {
      const startX = Math.random() * w;
      const startY = randomY ? Math.random() * h : -10;
      const baseAngle = Math.PI / 2 + (Math.random() - 0.5) * 0.6;
      const length = Math.random() * (cfg.size || 25) + (cfg.size || 25) * 0.8;
      const segs = Math.floor(Math.random() * 3) + 2;
      const segLen = length / segs;
      const path = [];
      for (let i = 0; i < segs; i++) {
        const ang = baseAngle + (Math.random() - 0.5) * 1.2;
        path.push({ dx: Math.cos(ang) * segLen, dy: Math.sin(ang) * segLen });
      }
      const colors = cfg.colors || ['190, 225, 255'];
      return {
        x: startX,
        y: startY,
        vx: 0,
        vy: (Math.random() * 0.4 + 0.2) * (cfg.speed || 1),
        color: colors[Math.floor(Math.random() * colors.length)],
        path,
        life: 0,
        maxLife: Math.random() * 0.5 + 0.3,
        baseOpacity: cfg.opacity || 1
      };
    },
    update: (p, ctx, _cfg, _w, h, recreate) => {
      p.y += p.vy;
      p.life += 0.025;
      if (p.life >= p.maxLife || p.y > h + 10) return recreate();
      const alpha = (1 - p.life / p.maxLife) * p.baseOpacity;
      ctx.save();
      ctx.strokeStyle = `rgba(${p.color}, ${alpha})`;
      ctx.shadowColor = `rgba(${p.color}, 0.9)`;
      ctx.shadowBlur = 8;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      for (let i = 0; i < p.path.length; i++) {
        ctx.lineTo(p.x + p.path[i].dx, p.y + p.path[i].dy);
      }
      ctx.stroke();
      ctx.restore();
    }
  },

  // snowflake: 六角雪花下落+呼吸+旋转（snow）
  snowflake: {
    create: (randomY, _ctx, cfg, w, h) => ({
      x: Math.random() * w,
      y: randomY ? Math.random() * h : -10,
      vx: (Math.random() - 0.5) * 0.3 * (cfg.speed || 1),
      vy: (Math.random() * 0.5 + 0.3) * (cfg.speed || 1),
      radius: Math.random() * (cfg.size || 2) + (cfg.size || 2),
      angle: Math.random() * Math.PI * 2,
      spinSpeed: (Math.random() - 0.5) * 0.02,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: Math.random() * 0.03 + 0.02,
      opacity: cfg.opacity || 0.85
    }),
    update: (p, ctx, _cfg, _w, h, recreate) => {
      p.x += p.vx + Math.sin(p.angle) * 0.2;
      p.y += p.vy;
      p.angle += p.spinSpeed;
      p.pulse += p.pulseSpeed;
      if (p.y > h + 10) return recreate();
      const breathing = 1 + Math.sin(p.pulse) * 0.3;
      const r = p.radius * breathing;
      ctx.save();
      ctx.globalAlpha = p.opacity;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.shadowColor = 'rgba(180, 220, 255, 0.8)';
      ctx.shadowBlur = 4;
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      for (let i = 0; i < 6; i++) {
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, -r * 2);
        ctx.moveTo(0, -r * 1.3);
        ctx.lineTo(-r * 0.5, -r * 1.7);
        ctx.moveTo(0, -r * 1.3);
        ctx.lineTo(r * 0.5, -r * 1.7);
        ctx.stroke();
        ctx.rotate(Math.PI / 3);
      }
      ctx.restore();
    }
  },

  // ink: 墨水扩散+条纹（ink）
  ink: {
    create: (randomY, _ctx, cfg, w, h) => {
      const isStreak = Math.random() > 0.7;
      const baseRadius = Math.random() * (cfg.size || 8) + (cfg.size || 8) * 0.5;
      return {
        x: Math.random() * w,
        y: randomY ? Math.random() * h : Math.random() * h,
        vx: (Math.random() - 0.5) * 0.1 * (cfg.speed || 1),
        vy: (Math.random() - 0.5) * 0.1 * (cfg.speed || 1),
        radius: baseRadius,
        maxRadius: baseRadius * 3,
        opacity: Math.random() * (cfg.opacity || 0.3) + 0.2,
        expandSpeed: Math.random() * 0.15 + 0.05,
        isStreak,
        streakAngle: Math.random() * Math.PI * 2
      };
    },
    update: (p, ctx, _cfg, _w, _h, recreate) => {
      p.x += p.vx;
      p.y += p.vy;
      p.radius += p.expandSpeed;
      p.opacity -= 0.008;
      if (p.opacity <= 0 || p.radius >= p.maxRadius) return recreate();
      ctx.save();
      if (p.isStreak) {
        ctx.translate(p.x, p.y);
        ctx.rotate(p.streakAngle);
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, p.radius * 2);
        grad.addColorStop(0, `rgba(20, 20, 20, ${p.opacity})`);
        grad.addColorStop(0.6, `rgba(40, 40, 40, ${p.opacity * 0.4})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(0, 0, p.radius * 2, p.radius * 0.6, 0, 0, Math.PI * 2);
        ctx.fill();
      } else {
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius);
        grad.addColorStop(0, `rgba(20, 20, 20, ${p.opacity})`);
        grad.addColorStop(0.6, `rgba(40, 40, 40, ${p.opacity * 0.4})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  },

  // firework: 烟花爆炸+重力+拖尾（firework）
  firework: {
    create: (randomY, _ctx, cfg, w, h) => {
      const palettes = cfg.colors && cfg.colors.length > 0 ? cfg.colors : ['255, 215, 0', '255, 69, 0', '255, 20, 147', '0, 255, 127'];
      return {
        x: Math.random() * w,
        y: randomY ? Math.random() * h : Math.random() * h,
        vx: (Math.random() - 0.5) * 2 * (cfg.speed || 1),
        vy: (Math.random() - 0.5) * 2 * (cfg.speed || 1),
        radius: Math.random() * (cfg.size || 1.5) + (cfg.size || 1.5) * 0.6,
        life: 0,
        maxLife: Math.random() * 0.4 + 0.4,
        color: palettes[Math.floor(Math.random() * palettes.length)],
        prevX: 0,
        prevY: 0,
        baseOpacity: cfg.opacity || 1
      };
    },
    update: (p, ctx, _cfg, _w, _h, recreate) => {
      p.prevX = p.x;
      p.prevY = p.y;
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.02;
      p.vx *= 0.98;
      p.vy *= 0.98;
      p.life += 0.03;
      if (p.life >= p.maxLife) return recreate();
      const alpha = (1 - p.life / p.maxLife) * p.baseOpacity;
      ctx.save();
      ctx.strokeStyle = `rgba(${p.color}, ${alpha * 0.6})`;
      ctx.lineWidth = p.radius * 0.8;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(p.prevX, p.prevY);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      ctx.fillStyle = `rgba(${p.color}, ${alpha})`;
      ctx.shadowColor = `rgba(${p.color}, 0.9)`;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
};

// 全局样式注入：为指定 skinId 注入动态 CSS（页面级共享，仅注入一次）
function injectSkinStylesOnce(skinId, vc) {
  if (!skinId || skinId === 'skin-default' || !vc) return;
  if (_injectedSkinStyles.has(skinId)) return;
  const cssText = buildSkinCssText(skinId, vc);
  if (!cssText || cssText.trim().length === 0) return;
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-td-skin', skinId);
  styleEl.textContent = cssText;
  document.head.appendChild(styleEl);
  _injectedSkinStyles.add(skinId);
}

// 强制重新注入：移除旧 <style> 后重新写入（用于管理端预览 visualConfig 实时变化）
function forceInjectSkinStyles(skinId, vc) {
  if (!skinId || skinId === 'skin-default' || !vc) return;
  const oldEl = document.querySelector(`style[data-td-skin="${skinId}"]`);
  if (oldEl) oldEl.remove();
  const cssText = buildSkinCssText(skinId, vc);
  if (!cssText || cssText.trim().length === 0) return;
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-td-skin', skinId);
  styleEl.textContent = cssText;
  document.head.appendChild(styleEl);
  _injectedSkinStyles.add(skinId);
}

export default {
  name: 'ThemeDecorator',
  props: {
    skinId: {
      type: String,
      default: 'skin-default'
    },
    size: {
      type: String,
      default: 'sm' // 'sm' (comments/chat) or 'lg' (user profile) or custom numeric string e.g. "48", "120"
    },
    /**
     * 可选：直接传入 visualConfig（优先级高于全局缓存）。
     * 用于管理端实时预览等需要覆盖缓存的场景。
     */
    visualConfig: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      canvasRef: null,
      ctx: null,
      animationFrameId: null,
      particles: [],
      canvasWidth: 0,
      canvasHeight: 0
    }
  },
  computed: {
    effectiveConfig() {
      if (this.visualConfig) return this.visualConfig;
      return skinVisualConfigCache.getConfig(this.skinId);
    },
    showCanvas() {
      return !!(this.effectiveConfig && this.effectiveConfig.particles && this.effectiveConfig.particles.preset);
    },
    badgeText() {
      const vc = this.effectiveConfig;
      if (!vc || !vc.badge || !vc.badge.text) return '';
      return vc.badge.text;
    },
    avatarDimensions() {
      let imgSize = 40;
      if (this.size === 'sm') {
        imgSize = 40;
      } else if (this.size === 'lg') {
        imgSize = 82;
      } else {
        const parsed = parseInt(this.size);
        if (!isNaN(parsed)) {
          imgSize = parsed;
        }
      }

      let offset = 18;
      if (imgSize <= 45) {
        offset = 16;
      } else if (imgSize <= 84) {
        offset = 20;
      } else {
        offset = 26;
      }

      return {
        imgSize: `${imgSize}px`,
        containerSize: `${imgSize + offset}px`
      };
    },
    decoratorStyles() {
      const dims = this.avatarDimensions;
      return {
        '--avatar-img-size': dims.imgSize,
        '--avatar-container-size': dims.containerSize
      };
    }
  },
  watch: {
    skinId: {
      handler() {
        this.ensureStylesInjected();
        this.$nextTick(() => {
          this.initCanvas();
        });
      },
      immediate: true
    },
    visualConfig: {
      handler() {
        this.ensureStylesInjected();
        this.$nextTick(() => {
          this.initCanvas();
        });
      },
      deep: true
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.ensureStylesInjected();
    this.initCanvas();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.stopAnimation();
  },
  methods: {
    ensureStylesInjected() {
      const vc = this.effectiveConfig;
      if (!vc) return;
      // 若父级传入 visualConfig prop（管理端预览等），每次变化都强制重新注入；
      // 否则仅注入一次（页面级共享）。
      if (this.visualConfig) {
        forceInjectSkinStyles(this.skinId, vc);
      } else {
        injectSkinStylesOnce(this.skinId, vc);
      }
    },
    handleResize() {
      this.resizeCanvas();
    },
    initCanvas() {
      this.stopAnimation();

      const canvas = this.$refs.canvasRef;
      if (!canvas) return;

      this.ctx = canvas.getContext('2d');
      this.resizeCanvas();
      this.initParticles();
      this.startAnimation();
    },
    resizeCanvas() {
      const canvas = this.$refs.canvasRef;
      if (!canvas) return;

      const rect = canvas.parentElement.getBoundingClientRect();
      this.canvasWidth = rect.width || 120;
      this.canvasHeight = rect.height || 60;

      canvas.width = this.canvasWidth;
      canvas.height = this.canvasHeight;
    },
    stopAnimation() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    },
    startAnimation() {
      const draw = () => {
        if (!this.ctx || !this.showCanvas) return;

        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.updateAndDrawParticles();
        this.animationFrameId = requestAnimationFrame(draw);
      };
      this.animationFrameId = requestAnimationFrame(draw);
    },
    initParticles() {
      this.particles = [];
      const count = this.getParticleCount();
      for (let i = 0; i < count; i++) {
        this.particles.push(this.createParticle(true));
      }
    },
    getParticleCount() {
      const pc = this.effectiveConfig && this.effectiveConfig.particles;
      if (!pc) return 0;
      const c = parseInt(pc.count);
      if (!isNaN(c) && c >= 0) return Math.min(c, 30); // 上限 30 保护性能
      return 0;
    },
    getParticleCfg() {
      const pc = (this.effectiveConfig && this.effectiveConfig.particles) || {};
      const speed = (typeof pc.speed === 'number' ? pc.speed : 1) * PARTICLE_SPEED_SCALE;
      return { ...pc, speed };
    },
    getPresetRenderer() {
      const cfg = this.getParticleCfg();
      const preset = cfg.preset;
      return PRESET_RENDERERS[preset] || null;
    },
    createParticle(randomY = false) {
      const renderer = this.getPresetRenderer();
      if (!renderer) return {};
      return renderer.create(randomY, this.ctx, this.getParticleCfg(), this.canvasWidth, this.canvasHeight);
    },
    updateAndDrawParticles() {
      const ctx = this.ctx;
      const w = this.canvasWidth;
      const h = this.canvasHeight;
      const renderer = this.getPresetRenderer();
      if (!renderer) return;
      const cfg = this.getParticleCfg();

      this.particles.forEach((p, idx) => {
        const recreate = () => {
          this.particles[idx] = this.createParticle(false);
          return undefined;
        };
        const result = renderer.update(p, ctx, cfg, w, h, recreate);
        if (result !== undefined) {
          this.particles[idx] = result;
        }
      });
    }
  }
}
</script>

<style scoped>
.theme-decorator-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.theme-decorator-container.has-canvas {
  overflow: hidden;
  border-radius: 12px;
}

/* Canvas background alignment */
.decorator-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.decorator-content-wrapper {
  position: relative;
  z-index: 1;
  width: 100%;
}

/* Avatar Frame Positioning & Adjustments */
.avatar-frame-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  width: var(--avatar-container-size, 58px);
  height: var(--avatar-container-size, 58px);
  flex-shrink: 0;
  aspect-ratio: 1 / 1;
}

.avatar-frame-wrapper.sm {
  width: var(--avatar-container-size, 58px);
  height: var(--avatar-container-size, 58px);
}

.avatar-frame-wrapper.lg {
  width: var(--avatar-container-size, 108px);
  height: var(--avatar-container-size, 108px);
}

/* Make inner slot elements round and strictly sized to avatar-img-size */
.avatar-frame-wrapper :deep(img),
.avatar-frame-wrapper :deep(.avatar),
.avatar-frame-wrapper :deep(.avatar-img),
.avatar-frame-wrapper :deep(.user-avatar-small),
.avatar-frame-wrapper :deep(.default-avatar),
.avatar-frame-wrapper :deep(.avatar-fallback),
.avatar-frame-wrapper :deep(.avatar-fallback-letter),
.avatar-frame-wrapper :deep(> img),
.avatar-frame-wrapper :deep(> div:not(.avatar-edit-overlay)) {
  border-radius: 50% !important;
  object-fit: cover;
  transition: all 0.3s;
  width: var(--avatar-img-size, 40px) !important;
  height: var(--avatar-img-size, 40px) !important;
  max-width: var(--avatar-img-size, 40px) !important;
  max-height: var(--avatar-img-size, 40px) !important;
  box-sizing: border-box;
}

/* Nickname Styling */
.nickname-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 2;
  position: relative;
  line-height: 1.4;
}

.nickname-display {
  font-weight: 700;
  display: inline-block;
  line-height: 1.4;
  vertical-align: middle;
}

.sm .nickname-display {
  font-size: 0.95rem;
}

.lg .nickname-display {
  font-size: 1.6rem;
}

/* 聊天气泡基础过渡（视觉样式由动态注入的 CSS 提供） */
.theme-decorator-container :deep(.chat-bubble-custom) {
  position: relative;
  transition: background 0.3s, border-color 0.3s, box-shadow 0.3s;
}

/* 移动端适配 */
@media (max-width: 480px) {
  .theme-decorator-container {
    flex-shrink: 0;
  }
}
</style>
